import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { AssignorBillingAddressData, isBillingAddressComplete } from '../../components/form/AssignorBillingAddress';

const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

interface UseContractActionsParams {
    apiUrl: string;
    apiKey: string;
    creditOperationId?: string;
    setLoading: (value: boolean) => void;
    hasUnsavedChanges?: boolean;
    hasInvalidDueDates?: boolean;
    validateAllFields?: () => boolean;
    setIsCancelModalOpen: (value: boolean) => void;
    setIsFinishModalOpen: (value: boolean) => void;
    onRefresh?: () => void;
    contractStatus?: string;
    billingAddress?: AssignorBillingAddressData | null;
    requiresBillingAddress?: boolean;
}

export const useContractActions = ({
    apiUrl,
    apiKey,
    creditOperationId,
    setLoading,
    hasUnsavedChanges = false,
    hasInvalidDueDates = false,
    validateAllFields,
    setIsCancelModalOpen,
    setIsFinishModalOpen,
    onRefresh,
    contractStatus,
    billingAddress,
    requiresBillingAddress = false,
}: UseContractActionsParams) => {
    const navigate = useNavigate();

    // Executa a solicitação de registro propriamente dita (POST). Isolada para
    // ser reutilizada tanto pela ação standalone "Solicitar Registro" quanto
    // pelo encadeamento após a finalização do contrato. Não valida status —
    // os chamadores fazem suas próprias pré-condições. Retorna true em sucesso.
    const requestRegister = useCallback(async (token: string): Promise<boolean> => {
        const response = await fetch(`${apiUrl}/contracts/operations/${creditOperationId}/request-register`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'x-api-key': apiKey || '',
                'Content-Type': 'application/json'
            }
        });
        if (response.ok) {
            toast.success('Registro solicitado com sucesso. Aguarde o processamento.');
            return true;
        }
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || 'Erro ao solicitar registro do contrato');
        return false;
    }, [apiUrl, apiKey, creditOperationId]);

    const handleCancelContract = useCallback(async () => {
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsCancelModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, setIsCancelModalOpen, navigate]);

    const handleFinishContract = useCallback(async () => {
        if (hasUnsavedChanges) {
            toast.error('Existem alterações não salvas. Salve antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        if (hasInvalidDueDates) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        if (validateAllFields && !validateAllFields()) {
            setIsFinishModalOpen(false);
            return;
        }

        if (requiresBillingAddress && !isBillingAddressComplete(billingAddress)) {
            toast.error('Preencha o endereço de faturamento completo antes de finalizar o contrato.');
            setIsFinishModalOpen(false);
            return;
        }

        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/${creditOperationId}/status/SUCCESSFULLY_COMPLETED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || ''
                }
            });
            if (!response.ok) {
                toast.error('Erro ao finalizar contrato');
                return;
            }

            // Etapa 1 concluída: contrato finalizado.
            toast.success('Contrato finalizado com sucesso!');

            // Etapa 2 encadeada: solicitar o registro na mesma ação.
            // O contrato acabou de ser finalizado, então a pré-condição de status
            // está satisfeita (não dependemos do contractStatus em memória).
            toast.info('Solicitando registro do contrato...');
            const registered = await requestRegister(token);
            if (!registered) {
                // Finalização OK, mas registro falhou: não navega, permite nova
                // tentativa de "Solicitar Registro" a partir da própria tela.
                onRefresh?.();
                return;
            }

            navigate(CONTRACTS_LIST_URL);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            setIsFinishModalOpen(false);
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, hasUnsavedChanges, hasInvalidDueDates, validateAllFields, setIsFinishModalOpen, navigate, billingAddress, requiresBillingAddress, requestRegister, onRefresh]);

    const handleBillingProcess = useCallback(async () => {
        if (!creditOperationId) {
            toast.error('Contrato não identificado');
            return;
        }
        if (hasUnsavedChanges) {
            toast.error('Existem alterações não salvas. Salve o contrato antes de faturar.');
            return;
        }
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${apiUrl}/contracts/billing/process`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ credit_operation_id: creditOperationId })
            });
            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                if (data.message?.includes('Faturamento iniciado')) {
                    toast.warning('Faturamento solicitado com sucesso. Aguarde o processamento');
                } else {
                    toast.error(data.message || 'Erro ao processar faturamento');
                }
            } else {
                const data = await response.json().catch(() => ({}));
                toast.error(data.message || 'Erro ao processar faturamento');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            onRefresh?.();
        }
    }, [apiUrl, apiKey, creditOperationId, setLoading, onRefresh, hasUnsavedChanges]);

    const handleRequestRegister = useCallback(async () => {
        if (!creditOperationId) {
            toast.error('Contrato não identificado');
            return;
        }
        if (contractStatus !== 'Finalizado com sucesso') {
            toast.error('A operação precisa estar finalizada com sucesso para solicitar o registro.');
            return;
        }
        if (requiresBillingAddress && !isBillingAddressComplete(billingAddress)) {
            toast.error('Preencha o endereço de faturamento completo antes de solicitar o registro.');
            return;
        }
        try {
            setLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            await requestRegister(token);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setLoading(false);
            onRefresh?.();
        }
    }, [setLoading, onRefresh, creditOperationId, contractStatus, billingAddress, requiresBillingAddress, requestRegister]);

    return { handleCancelContract, handleFinishContract, handleBillingProcess, handleRequestRegister };
};
